import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogIn, ShieldCheck } from "lucide-react";
import { login } from "@/lib/adminApi";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function AdminLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const { admin, setAdmin } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const data = await login(email, password);
      setAdmin(data.admin);
      const dest = loc.state?.from || "/admin";
      nav(dest, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível fazer login.";
      toast.error(typeof msg === "string" ? msg : "Erro no login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#F8FAFC] grid lg:grid-cols-2"
    >
      {/* Left panel */}
      <div className="hidden lg:flex bg-[#0F172A] text-white p-14 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{
               backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
               backgroundSize: "24px 24px",
             }}
        />
        <div className="relative z-10">
          <img
            src="/brand/hub-logo.webp?v=2"
            alt="HUB Conecta Empresarial"
            className="h-16 w-auto rounded-md"
          />
          <div className="mt-14 max-w-md">
            <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">
              Painel HUB
            </div>
            <h1 className="mt-3 font-display font-medium text-4xl leading-tight tracking-tight">
              Área administrativa do Diagnóstico HUB.
            </h1>
            <p className="mt-5 text-slate-300 leading-relaxed">
              Acesse os diagnósticos realizados, acompanhe o pipeline comercial
              e exporte os resultados para análise estratégica.
            </p>
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-3 text-xs text-slate-400">
          <ShieldCheck size={14} className="text-[#D97706]" />
          Acesso restrito · conteúdo confidencial
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-10 lg:py-0">
        <form
          onSubmit={submit}
          className="w-full max-w-md hub-card p-8 sm:p-10"
        >
          <div className="hub-eyebrow">Entrar no Painel HUB</div>
          <h2 className="mt-3 font-display font-medium text-2xl tracking-tight">
            Faça login para continuar
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Use suas credenciais de administrador.
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
                E-mail
              </span>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@hubconecta.com"
                className="hub-input"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2">
                Senha
              </span>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="hub-input"
              />
            </label>
          </div>

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="hub-btn-primary w-full justify-center mt-8"
          >
            {loading ? (
              <>
                <Lock size={16} /> Autenticando…
              </>
            ) : (
              <>
                <LogIn size={16} /> Entrar
              </>
            )}
          </button>

          <div className="mt-6 text-xs text-slate-500 text-center">
            Painel restrito · uso administrativo interno
          </div>
        </form>
      </div>
    </div>
  );
}
